/* ─────────────────────────────────────────────────────────────
   DELIVERY LOOKUP — address to a real figure
   #123 phase 2 · dev-build 76
───────────────────────────────────────────────────────────────

   Turns a typed address into the delivery price for the current cart, using
   Google for the distance and the rate card for the money.

   ── THE DIVISION OF LABOUR, AND WHY ────────────────────────────────────────
   Liquid picks the rate band. This file only multiplies.

   Choosing the band is the one piece of real logic — the highest weight_min_kg
   at or below the cart weight, which is NOT "min <= kg <= max" (a 34.5 kg cart
   satisfies neither 0-34 nor 35-49 and would fall through). Implementing that
   a second time here is the two-owners fault this project keeps finding, so
   delivery-estimate.liquid emits the chosen band's figures as data attributes
   and this file does arithmetic on them. Nothing else.

   ── WHY IT ROUNDS TO WHOLE KILOMETRES ──────────────────────────────────────
   Zapiet Rates by Distance charges on driving distance rounded to the nearest
   kilometre (confirmed 17-09-2026). Quoting 8.4 km when the app charges for 8
   is 80c of disagreement created by us, for nothing. So we round the same way.

   Driving distance, not straight line, for the same reason: straight line runs
   20-40% short in a city, and every quote would be under the charge.

   ── WHY THE FIELD DISAPPEARS WITHOUT A KEY ─────────────────────────────────
   No key means no autocomplete AND no distance, so the field could not do
   anything. Hiding it is honest; leaving it visible invites the customer to
   type into a box that silently cannot answer. The range from the rate cards
   still shows — that has never needed a key.

   ── COST IS CONTROLLED HERE, NOT IN THE BUDGET ─────────────────────────────
   Autocomplete bills per request when a session is abandoned, and most are.
   Firing per keystroke from the first character makes ~10 requests an address;
   a 350ms debounce and a 4-character floor makes 2-3. That constant is the
   cost control, which is why it is named and commented rather than inlined.
*/

(function () {
    'use strict';

    var DEBOUNCE_MS = 350;
    var MIN_CHARS   = 4;

    /*  One session token per typing session, reset after a pick. Google treats a
        token's requests as one session, which is what keeps keystrokes off the
        per-request rate when the session terminates properly. */
    function newToken() {
        if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
        return 'djw-' + Date.now() + '-' + Math.random().toString(16).slice(2);
    }

    /*  ── THE COUNTRY COMES OFF THE DISPLAYED ADDRESS ─────────────────────────
        Google returns "67 Galada Avenue, Parkville VIC, Australia". Ben, 17-09-2026:
        show street, suburb and state — the country is noise on an Australian store
        where every branch delivers inside a 50km radius anyway.

        Worth being straight about the postcode. Ben's stated format ends with one and
        the autocomplete response does not carry it: a suggestion gives mainText
        ("67 Galada Avenue") and secondaryText ("Parkville VIC, Australia"), nothing
        more. Getting a postcode means a second billed Place Details call per address,
        so it is not added on my own judgement — raised in chat instead.

        Matched on a TRAILING country only, so a street legitimately containing the
        word is untouched. Covers the spelled-out name and the two-letter code. */
    function tidyAddress(s) {
        return String(s || '')
            .replace(/,\s*(australia|au)\s*$/i, '')
            .replace(/\s*,\s*$/, '')
            .trim();
    }

    function money(dollars) {
        var cents = Math.round(dollars * 100);
        /*  The drawer's own formatter, so this file cannot disagree with the rest of
            the drawer about how money looks. It is global by the time anything here
            runs — both scripts are deferred and this one is never called on load. */
        if (typeof window.pcdFormatMoney === 'function') return window.pcdFormatMoney(cents);
        return '$' + (cents % 100 === 0 ? (cents / 100).toFixed(0) : (cents / 100).toFixed(2));
    }

    function readRates(el) {
        if (!el || el.dataset.deReady !== '1') return null;
        var n = function (k) { return parseFloat(el.dataset[k]); };
        var r = {
            origin:  el.dataset.deOrigin || '',
            phone:   el.dataset.dePhone || '',
            baseOne: n('deBaseOne'), kmOne: n('deKmOne'),
            baseRt:  n('deBaseRt'),  kmRt:  n('deKmRt'),
            maxKm:   n('deMaxKm')
        };
        var bad = ['baseOne', 'kmOne', 'baseRt', 'kmRt'].some(function (k) { return isNaN(r[k]); });
        if (bad || !r.origin) return null;
        return r;
    }

    /* ── Google calls ──────────────────────────────────────────────────────── */

    function suggest(key, country, input, token, bias) {
        var body = {
            input: input,
            includedRegionCodes: [country.toLowerCase()],
            sessionToken: token
        };
        /*  Bias, not restriction. A Melbourne customer sees Melbourne suburbs first,
            but someone delivering interstate is not blocked from finding their
            address — a restriction would refuse it outright. */
        if (bias) body.locationBias = bias;

        return fetch('https://places.googleapis.com/v1/places:autocomplete', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': key },
            body: JSON.stringify(body)
        }).then(function (r) {
            if (!r.ok) throw new Error('autocomplete ' + r.status);
            return r.json();
        }).then(function (d) {
            return (d.suggestions || []).map(function (s) {
                var p = s.placePrediction || {};
                var f = p.structuredFormat || {};
                return {
                    placeId: p.placeId,
                    main: (f.mainText && f.mainText.text) || (p.text && p.text.text) || '',
                    sub:  (f.secondaryText && f.secondaryText.text) || '',
                    full: (p.text && p.text.text) || ''
                };
            }).filter(function (s) { return s.placeId && s.main; });
        });
    }

    function distanceKm(key, originAddress, destination) {
        /*  destination is { placeId } from a suggestion, or { address } from the
            Check button when someone typed a full address and pressed it. */
        var dest = destination.placeId
            ? { waypoint: { placeId: destination.placeId } }
            : { waypoint: { address: destination.address } };

        return fetch('https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': key,
                /*  Required. Without a field mask the Routes API refuses the request
                    rather than returning everything. */
                'X-Goog-FieldMask': 'originIndex,destinationIndex,distanceMeters,condition'
            },
            body: JSON.stringify({
                origins: [{ waypoint: { address: originAddress } }],
                destinations: [dest],
                travelMode: 'DRIVE'
            })
        }).then(function (r) {
            if (!r.ok) throw new Error('routematrix ' + r.status);
            return r.json();
        }).then(function (rows) {
            var row = (rows || []).filter(function (x) {
                return x.condition === 'ROUTE_EXISTS' && typeof x.distanceMeters === 'number';
            })[0];
            if (!row) throw new Error('no route');
            /*  Nearest whole kilometre, matching Zapiet. Math.round, not floor:
                floor would systematically under-quote by up to a kilometre. */
            return Math.round(row.distanceMeters / 1000);
        });
    }

    /* ── Wiring one address field ──────────────────────────────────────────── */

    /*  ── SURFACE-AGNOSTIC AS OF DEV-BUILD 78 ─────────────────────────────────
        This took a root element and looked inside it for .pb-cart-drawer-fulfil by
        name, which is why the feature could only ever exist in the drawer. The cart
        page needed the same field, and Ben's requirement was one answer shared
        between them rather than two implementations.

        So it keys on [data-de-scope] instead. Any surface that renders a scope
        carrying the key and the rate figures, with [data-de-figure] and [data-de-sub]
        inside it, gets the feature — and adding a third surface later is markup only.

        wire(root) now walks every scope under root, so both callers pass document and
        neither needs to know what the other renders. */
    function wire(root) {
        var scopes = (root || document).querySelectorAll('[data-de-scope]');
        for (var i = 0; i < scopes.length; i++) wireScope(scopes[i]);
    }

    function wireScope(scope) {
        var block = scope.querySelector('[data-de-addr]');
        if (!block || block.dataset.deWired === '1') return;

        var key     = scope.dataset.deKey || '';
        var country = scope.dataset.deCountry || 'AU';
        var fulfil  = scope;
        var rates   = readRates(fulfil);

        /*  ── WHY IT IS HIDDEN, OUT LOUD ──────────────────────────────────────
            Four different causes produced the same silent absence, which made a
            missing address field impossible to diagnose from outside the store: a key
            was added, nothing appeared, and there was no way to tell which cause.

            Each now names itself in the console. It stays a console warning rather
            than anything on screen — a customer must never be shown our configuration
            problems, and the range beside it is a correct answer in all four states. */
        if (!key) {
            console.warn('[DJW delivery] Address lookup off: no API key. Theme settings > Delivery estimate.');
            block.hidden = true;
            return;
        }
        /*  The old "no fulfil block" branch is gone: the scope IS that element now, so
            reaching this function at all means it exists. One fewer impossible state
            to explain in the console. */
        if (fulfil.dataset.deReady !== '1') {
            console.warn('[DJW delivery] Address lookup off: delivery-estimate emitted no rate data. Either the cart has no weight, or the chosen location has no delivery_rates attached, or no rate card covers this weight.', { location: fulfil.dataset.deOrigin || '(none)' });
            block.hidden = true;
            return;
        }
        if (!rates) {
            console.warn('[DJW delivery] Address lookup off: rate data present but unusable.', {
                origin: fulfil.dataset.deOrigin,
                baseOne: fulfil.dataset.deBaseOne,
                kmOne: fulfil.dataset.deKmOne,
                baseRt: fulfil.dataset.deBaseRt,
                kmRt: fulfil.dataset.deKmRt
            });
            block.hidden = true;
            return;
        }
        block.hidden = false;

        var input    = block.querySelector('[data-de-input]');
        var panel    = block.querySelector('[data-de-panel]');
        var result   = block.querySelector('[data-de-result]');
        var resolved = block.querySelector('[data-de-resolved]');
        var error    = block.querySelector('[data-de-error]');
        var figure   = scope.querySelector('[data-de-figure]');
        var sub      = scope.querySelector('[data-de-sub]');
        if (!input || !panel) return;

        /*  ── A STALE ANSWER IS CLEARED, NOT LEFT ON SCREEN ───────────────────
            delivery-estimate sets data-de-stale when a stored distance no longer
            applies to the chosen branch — either measured from elsewhere, or now
            outside that branch's radius.

            The stored answer is wiped rather than merely hidden. Leaving it in the
            cart would carry a Melbourne distance onto a Sydney order, and this data
            exists precisely so it can reach the order later. A record that no longer
            means anything is worse than no record.

            The field resets to empty so the customer can simply re-enter, which is
            the only thing they can usefully do. No error: they did nothing wrong,
            and the range beside it is a correct answer. */
        if (scope.dataset.deStale === '1') {
            /*  Cleared on every surface, not just this one. A stale answer left
                showing on the cart while the drawer has reset is the same disagreement
                the sync above exists to prevent, in the other direction. */
            var others = document.querySelectorAll('[data-de-addr]');
            for (var k = 0; k < others.length; k++) {
                others[k].classList.remove('is-resolved');
                var or = others[k].querySelector('[data-de-result]');
                var oi = others[k].querySelector('[data-de-input]');
                if (or) or.hidden = true;
                if (oi) oi.value = '';
            }
            block.classList.remove('is-resolved');
            if (result) result.hidden = true;
            if (input) input.value = '';
            if (typeof window.writeCartAttributes === 'function') {
                window.writeCartAttributes({
                    'Delivery address': '',
                    'Delivery distance km': '',
                    'Delivery estimate': '',
                    '_delivery_measured_from': ''
                });
            }
        }

        var token = newToken();
        var timer = null;
        var items = [];
        var active = -1;
        var busy = false;

        function showError(msg) {
            error.textContent = msg;
            error.hidden = false;
        }
        function clearError() { error.hidden = true; }

        function closePanel() {
            panel.hidden = true;
            panel.textContent = '';
            input.setAttribute('aria-expanded', 'false');
            active = -1;
        }

        function render(list) {
            items = list;
            active = -1;
            panel.textContent = '';
            if (!list.length) { closePanel(); return; }
            /*  dj-, not pb-cart-drawer-. The markup became a shared snippet in
                dev-build 78 and these creations did not follow it, so the suggestion
                list would have rendered unstyled on BOTH surfaces — the cart has no
                rule for a drawer class, and the drawer's own rules stopped matching
                the moment the snippet was renamed.

                Renamed by global replace, not by a list of call sites: the hand list
                missed setActive's querySelectorAll and the audit caught it. */
            list.forEach(function (s, i) {
                var row = document.createElement('div');
                row.className = 'dj-addr-opt';
                row.setAttribute('role', 'option');
                row.dataset.index = String(i);
                var m = document.createElement('span');
                m.className = 'dj-addr-opt-main';
                m.textContent = s.main;
                var t = document.createElement('span');
                t.className = 'dj-addr-opt-sub';
                t.textContent = tidyAddress(s.sub);
                row.appendChild(m);
                row.appendChild(t);
                panel.appendChild(row);
            });
            panel.hidden = false;
            input.setAttribute('aria-expanded', 'true');
        }

        function setActive(i) {
            var opts = panel.querySelectorAll('.dj-addr-opt');
            if (!opts.length) return;
            if (i < 0) i = opts.length - 1;
            if (i >= opts.length) i = 0;
            active = i;
            opts.forEach(function (o) { o.classList.remove('is-active'); });
            opts[i].classList.add('is-active');
        }

        /*  ── OUTSIDE THE RANGE IS NOT A PRICE ────────────────────────────────
            Every rate card carries max_distance_km, which is how far that branch
            actually drives. Without this check the arithmetic keeps going: a 300km
            address got a confident figure for a delivery nobody would make, and the
            customer would find out at checkout or on the phone.

            So the distance is tested BEFORE anything is priced, and the failure names
            the two things they can actually do. The phone number comes from the
            location record when it has one; the message stands without it rather
            than printing an empty gap. */
        function outOfRange(km, label) {
            var msg = label + ' is ' + km + ' km away, outside the ' + rates.maxKm +
                      ' km we deliver to from this branch. You are welcome to collect' +
                      ' it yourself';
            msg += rates.phone
                ? ', or call us on ' + rates.phone + ' and we will see what we can do.'
                : ', or give us a call and we will see what we can do.';
            showError(msg);
            closePanel();
            /*  The range beside it is left alone. It is still true for addresses
                inside the radius, and blanking it would leave no price at all. */
        }

        /*  ── ONE LOOKUP UPDATES EVERY SURFACE ON THE PAGE ────────────────────
            Ben, 17-09-2026: changing the address in the drawer while standing on the
            cart page must update the cart without reloading it.

            Both surfaces render the same field and read the same cart attributes, so
            they already AGREED on reload. What they did not do is agree immediately —
            the drawer wrote the attribute and its own figure, and the cart kept the
            range until something re-rendered it.

            Every scope is updated in the DOM rather than firing pbcart:updated. The
            event would re-render the whole cart section for a change that touches
            three spans, and it would put a skeleton over figures that are already
            correct. The rate band is per surface, so each scope is priced from its
            OWN readRates — not from this one's — which is what keeps a cart and a
            drawer showing different weights from ever borrowing each other's card. */
        function syncAllScopes(km, label) {
            var all = document.querySelectorAll('[data-de-scope]');
            for (var i = 0; i < all.length; i++) {
                if (all[i] === scope) continue;
                var r = readRates(all[i]);
                if (!r) continue;
                if (r.maxKm && km > r.maxKm) continue;   /* out of ITS range, leave it */

                var f = all[i].querySelector('[data-de-figure]');
                var s = all[i].querySelector('[data-de-sub]');
                var one2 = r.baseOne + r.kmOne * km;
                var rt2  = r.baseRt  + r.kmRt  * km;
                writeFigure(all[i], f, one2, rt2);
                if (s) s.textContent = money(one2) + ' one way or ' + money(rt2) + ' both ways';

                /*  And its address block, so the other surface shows the resolved line
                    rather than an empty field the customer has already filled in. */
                var b = all[i].querySelector('[data-de-addr]');
                if (!b) continue;
                var inp = b.querySelector('[data-de-input]');
                var res = b.querySelector('[data-de-result]');
                var rsv = b.querySelector('[data-de-resolved]');
                var err = b.querySelector('[data-de-error]');
                if (inp) inp.value = label;
                if (rsv) rsv.textContent = label + ' · ' + km + ' km';
                if (res) res.hidden = false;
                if (err) err.hidden = true;
                b.classList.add('is-resolved');
            }
        }

        /*  ── ONE WRITER FOR THE FIGURE — 19-09-2026 ────────────────────────
            Both sites below used to do
              figure.textContent = money(one) + ' – ' + money(rt)
            which writes a RANGE, and writes it with textContent — so it also
            wipes any child markup inside the figure.

            The cart drawer's method block now renders
              <span data-de-figure>
                <span>from</span><span data-de-amount>$60</span><span>one-way</span>
              </span>
            and an address lookup would have flattened that to "$60 – $120",
            losing the words and contradicting the design in the state that
            matters most: after the customer has said where they are.

            Neither surface can just win — the cart page WANTS the range. So the
            SCOPE declares what it wants:

              data-de-figure-mode="one"  replace only [data-de-amount], leaving
                                         the surrounding words in place
              (absent)                   previous behaviour, byte for byte

            The cart page carries no flag, so nothing there changes. */
        function writeFigure(scopeEl, figureEl, oneCents, rtCents) {
            if (!figureEl) return;
            if (scopeEl && scopeEl.getAttribute('data-de-figure-mode') === 'one') {
                var amount = figureEl.querySelector('[data-de-amount]');
                if (amount) {
                    amount.textContent = money(oneCents);
                    /*  dev-build 189 — an affix that says "from" before an address is
                        checked carries its priced wording here. Only the cart page's
                        one-way pill has it; the drawer's affixes are untouched. */
                    var affix = figureEl.querySelector('[data-de-affix-priced]');
                    if (affix) affix.textContent = affix.getAttribute('data-de-affix-priced');
                    return;
                }
                /*  No amount node means the fallback state was rendered
                    ("Priced at checkout"). Rebuilding the whole figure is
                    correct there: a real price now exists where none did, so
                    the affixes have to be created rather than preserved. */
                figureEl.textContent = '';
                var a = document.createElement('span');
                a.className = 'pb-cart-drawer-fulfil-affix';
                a.textContent = 'from';
                var b = document.createElement('span');
                b.className = 'pb-cart-drawer-fulfil-amount';
                b.setAttribute('data-de-amount', '');
                b.textContent = money(oneCents);
                var c = document.createElement('span');
                c.className = 'pb-cart-drawer-fulfil-affix';
                c.textContent = 'one-way';
                figureEl.appendChild(a);
                figureEl.appendChild(b);
                figureEl.appendChild(c);
                return;
            }
            figureEl.textContent = money(oneCents) + ' – ' + money(rtCents);
        }

        function priceIt(km, label) {
            if (rates.maxKm && km > rates.maxKm) { outOfRange(km, label); return; }
            var one = rates.baseOne + rates.kmOne * km;
            var rt  = rates.baseRt  + rates.kmRt  * km;
            writeFigure(scope, figure, one, rt);
            syncAllScopes(km, label);
            /*  Spells out which figure is which. "One way, or both ways" left the
                reader to pair two numbers with two services by position. */
            if (sub) sub.textContent = money(one) + ' one way or ' + money(rt) + ' both ways';
            if (resolved) resolved.textContent = label + ' · ' + km + ' km';
            if (result) result.hidden = false;
            block.classList.add('is-resolved');
            closePanel();

            /*  Written to the cart, not held in this page. So the figure survives a
                reload, appears on the cart page too, and lands on the order where
                the team can see what the customer was quoted. Goes through
                charged-nights.js's writer, which serialises against every other
                cart write — the one-writer rule from #131. */
            if (typeof window.writeCartAttributes === 'function') {
                window.writeCartAttributes({
                    'Delivery address': label,
                    'Delivery distance km': String(km),
                    'Delivery estimate': money(one) + ' – ' + money(rt),
                    /*  ── WHAT IT WAS MEASURED FROM ──────────────────────────────
                        Without this, a distance measured from Port Melbourne would be
                        applied to Sydney the moment the customer switched branches —
                        a precise-looking figure for a trip nobody costed.

                        Recorded so the server can check the stored distance still
                        belongs to the chosen branch before trusting it. Same
                        discipline as the bond: the record says what it was derived
                        from, not only what the answer was. */
                    /*  Underscored, unlike the three above. Ben, 17-09-2026: this one
                        is diagnostic but will be pulled by the POS automation later.
                        A leading underscore hides a cart attribute from the order
                        screens and customer emails while leaving it fully readable
                        through the API — so ops are not asked to interpret a branch
                        address, and the integration still gets it. */
                    '_delivery_measured_from': rates.origin
                });
            }
        }

        function lookup(dest, label) {
            if (busy) return;
            busy = true;
            clearError();
            block.classList.add('is-busy');
            distanceKm(key, rates.origin, dest)
                .then(function (km) { priceIt(km, label); })
                .catch(function () {
                    /*  Degrades to the range rather than to nothing. The range is
                        always available, so a failed lookup costs precision and
                        never the answer. Same wording covers a dropped connection,
                        a refused key and an address with no road to it — the
                        customer can act on none of those distinctions. */
                    showError('We cannot find that address. The estimate above still applies.');
                    closePanel();
                })
                .then(function () {
                    busy = false;
                    block.classList.remove('is-busy');
                });
        }

        function commit(s) {
            /*  The tidied string is what the customer sees AND what is written to the
                cart, so the order shows the address they confirmed. The placeId still
                carries full precision to Google, so trimming the display costs nothing
                in accuracy. */
            input.value = tidyAddress(s.full || (s.main + ', ' + s.sub));
            lookup({ placeId: s.placeId }, input.value);
            token = newToken();   /* session terminated — next typing starts a new one */
        }

        input.addEventListener('input', function () {
            clearError();
            clearTimeout(timer);
            var q = input.value.trim();
            if (q.length < MIN_CHARS) { closePanel(); return; }
            timer = setTimeout(function () {
                suggest(key, country, q, token, null)
                    .then(render)
                    .catch(function () { closePanel(); });
            }, DEBOUNCE_MS);
        });

        input.addEventListener('keydown', function (e) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                if (panel.hidden) return;
                e.preventDefault();
                setActive(active + (e.key === 'ArrowDown' ? 1 : -1));
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();   /* never submit anything from in here */
                if (!panel.hidden && active >= 0 && items[active]) commit(items[active]);
                else if (input.value.trim().length >= MIN_CHARS) lookup({ address: input.value.trim() }, tidyAddress(input.value.trim()));
                return;
            }
            if (e.key === 'Escape' && !panel.hidden) { e.preventDefault(); closePanel(); }
        });

        /*  mousedown, not click: blur fires first on a click and would close the
            panel before the selection registered. */
        panel.addEventListener('mousedown', function (e) {
            var opt = e.target.closest('.dj-addr-opt');
            if (!opt) return;
            e.preventDefault();
            var s = items[Number(opt.dataset.index)];
            if (s) commit(s);
        });

        block.querySelector('[data-de-check]')?.addEventListener('click', function () {
            var q = input.value.trim();
            if (q.length < MIN_CHARS) { showError('Type a little more of the address.'); return; }
            lookup({ address: q }, tidyAddress(q));
        });

        block.querySelector('[data-de-change]')?.addEventListener('click', function () {
            block.classList.remove('is-resolved');
            if (result) result.hidden = true;
            clearError();
            input.focus();
            input.select();
        });

        input.addEventListener('blur', function () {
            setTimeout(function () {
                if (!block.contains(document.activeElement)) closePanel();
            }, 0);
        });

        block.dataset.deWired = '1';
    }

    window.DJWDelivery = { wire: wire };
})();
